import {
  addOrder,
  deleteOrder,
  fetchOrderByGuestId,
  updateOrder,
} from "redux/actions/order";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";
import EnumUtils from "utils/EnumUtils";
import OrderStatus from "types/enum/OrderStatus";
import { Button, Typography } from "@material-ui/core";
import { DateTime } from "luxon";
import OrderResponseStatus from "types/enum/OrderResponseStatus";
import PaymentType from "types/enum/PaymentType";
import { Link, useParams } from "react-router-dom";
import React, { useEffect } from "react";
import { FormType } from "components/FormField";
import OrderRes from "types/res/order/OrderRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import CreateOrderReq from "types/req/order/CreateOrderReq";
import UpdateOrderReq from "types/req/order/UpdateOrderReq";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const OrderListByGuest = () => {
  const { t } = useTranslation("orderList");
  const dispatch = useDispatch();
  const { guestId } = useParams<{ guestId: string }>();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const orders = useSelector((state) => state.order);
  const loginStaff = useSelector((state) => state.account.staff);

  useEffect(() => {
    if (!guestId) return;
    const guestIdToNumber = Number(guestId);
    if (Number.isNaN(guestIdToNumber)) return;
    dispatch(fetchOrderByGuestId(companyId, Number(guestIdToNumber)));
  }, [guestId]);

  const forms = [
    {
      label: t("forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
  ];
  const headers: {
    key: keyof OrderRes;
    label: string;
  }[] = [
    { key: "orderDate", label: t("columns.orderDate") },
    { key: "status", label: t("columns.status") },
    { key: "shopId", label: t("columns.shopId") },
    { key: "guestId", label: t("columns.guestName") },
    { key: "guestId", label: t("columns.guestCategory") },
    { key: "guestId", label: t("columns.guestTel") },
    { key: "castName", label: t("columns.castName") },
    { key: "orderAddress", label: t("columns.location") },
    { key: "courseId", label: t("columns.course") },
    { key: "options", label: t("columns.options") },
    { key: "courseTime", label: t("columns.courseTime") },
    { key: "areaId", label: t("columns.area") },
    { key: "orderAddress", label: t("columns.address1") },
    { key: "departureTime", label: t("columns.departureTime") },
    { key: "planInTime", label: t("columns.planInTime") },
    { key: "actualInTime", label: t("columns.actualInOut") },
    { key: "actualEndTime", label: t("columns.call") },
    { key: "responseStatus", label: t("columns.responseStatus") },
    { key: "collectReceivable", label: t("columns.collectReceivable") },
    { key: "totalFee", label: t("columns.totalFee") },
    { key: "paymentType", label: t("columns.paymentType") },
    { key: "payoff", label: t("columns.payoff") },
    { key: "createdBy", label: t("columns.createdBy") },
    { key: "updatedBy", label: t("columns.updatedBy") },
    { key: "updatedAt", label: t("columns.updatedAt") },
    { key: "orderId", label: "" },
  ];

  return (
    <OrderTable
      title={t("byGuest.title")}
      formId="orderForm"
      fullWidth
      forms={forms}
      rows={headers}
      data={orders}
      addFunc={(formData) => addOrder(companyId, formData)}
      updateFunc={(formData) => updateOrder(companyId, formData)}
      deleteFunc={(item) =>
        deleteOrder(companyId, {
          orderId: item.orderId,
        })
      }
      invisibleDeleteIcon={true}
      values={[
        (o) =>
          DateTimeUtils.toFormatAsLocalTimezone(
            o.orderDate,
            FORMAT_TYPE.DAY_LIST
          ),
        (o) => EnumUtils.mapToEnum(OrderStatus, o.status) || "",
        (o) => o.shop?.name || t("cellLabels.unset"),
        (o) => o.guest?.name || t("cellLabels.unset"),
        (o) =>
          loginStaff.company.guestCategoryShopFlag
            ? o.guest?.guestGuestCategoryShops?.find(
                (guestGuestCategoryShop) =>
                  guestGuestCategoryShop.shopId === o.shopId &&
                  guestGuestCategoryShop.guestId === o.guestId
              )?.guestCategory?.name || t("cellLabels.unset")
            : o?.guest?.guestCategory?.name || t("cellLabels.unset"),
        (o) => o.guest?.tel || t("cellLabels.unset"),
        (o) => o.castName?.name || t("cellLabels.unset"),
        (o) => o.hotel?.name || t("cellLabels.unset"),
        (o) => o.course?.name || t("cellLabels.unset"),
        (o) =>
          o.options?.map((option) => option.name).join(",") ||
          t("cellLabels.unset"),
        (o) =>
          o.courseTime ? `${o.courseTime}${t("units.minutes")}` : t("cellLabels.unset"),
        (o) => o.area?.name || t("cellLabels.unset"),
        (o) => o.orderAddress || t("cellLabels.unset"),

        (o) =>
          o.departureTime
            ? DateTimeUtils.toFormatAsLocalTimezone(
                o.departureTime,
                FORMAT_TYPE.TIME
              )
            : t("cellLabels.unset"),
        (o) =>
          o.planInTime
            ? DateTimeUtils.toFormatAsLocalTimezone(
                o.planInTime,
                FORMAT_TYPE.TIME
              )
            : t("cellLabels.unset"),
        (o) =>
          o.actualInTime && o.actualEndTime ? (
            `${DateTimeUtils.toFormatAsLocalTimezone(
              o.actualInTime,
              FORMAT_TYPE.TIME
            )} ~ ${DateTimeUtils.toFormatAsLocalTimezone(
              o.actualEndTime,
              FORMAT_TYPE.TIME
            )}`
          ) : o.requestActualInTime ? (
            <Typography style={{ color: "red" }} variant="body1">
              {t("cellLabels.requested")}
            </Typography>
          ) : (
            t("cellLabels.unset")
          ),
        (o) =>
          o?.planOutTime && o?.shop?.orderAlertEmailTime
            ? DateTime.fromJSDate(o.planOutTime)
                .minus({
                  minutes: o?.shop?.orderAlertEmailTime,
                })
                .toFormat(FORMAT_TYPE.TIME)
            : t("cellLabels.unset"),
        (o) =>
          EnumUtils.mapToEnum(OrderResponseStatus, o.responseStatus) ||
          t("cellLabels.unset"),
        (o) => (o.collectReceivable ? t("cellLabels.collected") : t("cellLabels.notCollected")),
        (o) => `${o.totalFee || 0}${t("units.yen")}`,
        (o) => EnumUtils.mapToEnum(PaymentType, o.paymentType) || t("cellLabels.unset"),
        (o) => (o.payoff ? t("cellLabels.settled") : t("cellLabels.unsettled")),
        (o) => o.createdBy?.name,
        (o) => o.updatedBy?.name,
        (o) =>
          DateTimeUtils.toFormatAsLocalTimezone(
            o.updatedAt,
            FORMAT_TYPE.DATE_TIME
          ),
        (o) => (
          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to={o.orderId ? `/order/${o.orderId}` : `/order`}
          >
            {t("actions.edit")}
          </Button>
        ),
      ]}
    />
  );
};
const OrderTable = styled<
  React.FC<CommonTableProps<OrderRes, CreateOrderReq, UpdateOrderReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default OrderListByGuest;
